import { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CircleDot, Link2, SquareAsterisk } from 'lucide-react';
import { ChapterMarker, RevealText } from './shared';
import { BROWN } from '../assets';

const HARDWARE = [
  {
    icon: Link2,
    name: 'YKK Excella Zippers',
    desc: 'Polished teeth, hand-set pulls. The zip glides — and clicks shut like a vault.',
  },
  {
    icon: CircleDot,
    name: 'Metal Snap Buttons',
    desc: 'Solid brass, gunmetal finish. Pressed at four tonnes so they never let go.',
  },
  {
    icon: SquareAsterisk,
    name: 'Buckles & Trims',
    desc: 'Nickel-free alloys, tumble-polished. Details buyers notice on the tenth wear.',
  },
];

/* ── 3D Tilt Card ─────────────────────────────────────────── */
function TiltCard({ children, className, ...props }) {
  const cardRef = useRef(null);

  const handleMove = useCallback((e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -8;
    const rotateY = ((x - centerX) / centerX) * 8;
    card.style.transform = `perspective(600px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  }, []);

  const handleLeave = useCallback(() => {
    const card = cardRef.current;
    if (card) card.style.transform = 'perspective(600px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
  }, []);

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={className}
      style={{ transition: 'transform 0.15s ease-out', willChange: 'transform', transformStyle: 'preserve-3d' }}
      {...props}
    >
      {children}
    </div>
  );
}

export default function Ch6Hardware() {
  return (
    <section id="ch-6" data-testid="chapter-6" className="relative min-h-screen bg-[#0A0A0A] px-6 md:px-16 py-28 md:py-40 overflow-hidden">
      <ChapterMarker num="06" title="Hardware" />
      <div className="md:grid md:grid-cols-12 md:gap-16 items-center">
        <div className="md:col-span-5 relative">
          <motion.div
            initial={{ clipPath: 'inset(0 100% 0 0)' }}
            whileInView={{ clipPath: 'inset(0 0% 0 0)' }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
            className="relative aspect-[4/5] overflow-hidden border border-[#2A2A2A]"
          >
            <img src={BROWN.zip} alt="Front zip of the brown suede bomber" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 to-transparent" />
          </motion.div>
          <span className="font-accent text-[9px] text-[#525252] block mt-4">FIG. 06 — FRONT ZIP, SUEDE BOMBER PT-2411</span>
        </div>

        <div className="md:col-span-7 mt-14 md:mt-0">
          <RevealText className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
            The metal that<br /><span className="italic text-[#B89B72]">makes it snap.</span>
          </RevealText>
          <div className="mt-12 space-y-4">
            {HARDWARE.map((h, i) => (
              <motion.div
                key={h.name}
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
              >
                <TiltCard
                  data-testid={`hardware-item-${i + 1}`}
                  data-hover
                  className="group flex items-start gap-6 border border-[#2A2A2A] bg-[#111111]/60 backdrop-blur-sm p-6 md:p-8 transition-colors duration-300 hover:border-[#8C5A35]/60"
                >
                  <div className="shrink-0 w-12 h-12 rounded-full border border-[#B89B72]/40 flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:rotate-[360deg]">
                    <h.icon className="w-5 h-5 text-[#B89B72]" strokeWidth={1.2} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-light text-[#F5F5F0] mb-1.5">{h.name}</h3>
                    <p className="font-body font-light text-sm text-[#A3A3A3] leading-relaxed">{h.desc}</p>
                  </div>
                  <span className="ml-auto font-accent text-[9px] text-[#525252] group-hover:text-[#B89B72] transition-colors duration-300 hidden md:block self-center">
                    ATTACHED ✓
                  </span>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
